import { useState } from 'react';
import { EyeClosedIcon, EyeIcon, PushPinSimpleIcon } from '@phosphor-icons/react';
import { Button } from '@admin-panel/ui';
import { motion, useReducedMotion } from 'motion/react';
import GroupImage from './GroupImage';
import LookGroupLooksPanel from './LookGroupLooksPanel';
import GroupMeta from './GroupMeta';
import { EASE_IN_OUT, LIST_SPRING, MOTION_DURATION } from './motion';
import type { LookGroupActionItemProps, ProductDepthSummary } from './types';

type AvailableGroupItemProps = LookGroupActionItemProps & {
  onPin: (groupId: number) => Promise<void>;
  elementRef?: (node: HTMLElement | null) => void;
  productDepth?: ProductDepthSummary;
};

const AvailableGroupItem = ({
  item,
  onPin,
  isBusy,
  itemVariants,
  elementRef,
  productDepth,
}: AvailableGroupItemProps) => {
  const reduceMotion = useReducedMotion();
  const [isLooksOpen, setIsLooksOpen] = useState(false);
  const previewTitle = item.preview?.title || `Look Group #${item.group.id}`;
  const previewImage = item.preview?.imageUrl;

  return (
    <motion.article
      ref={elementRef}
      variants={itemVariants}
      layout={reduceMotion ? false : 'position'}
      transition={
        reduceMotion
          ? { duration: 0 }
          : {
              layout: LIST_SPRING,
              y: { duration: MOTION_DURATION.micro, ease: EASE_IN_OUT },
            }
      }
      className="rounded-xl border border-border bg-card p-3"
    >
      <div className="flex items-start gap-3">
        <GroupImage title={previewTitle} imageUrl={previewImage} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="m-0 text-sm font-semibold text-foreground">{previewTitle}</p>
            </div>

            <Button
              size="small"
              variant="solid"
              icon={<PushPinSimpleIcon size={14} />}
              isLoading={isBusy}
              onClick={() => onPin(item.group.id)}
            >
              Pin Group
            </Button>
          </div>

          <GroupMeta item={item} productDepth={productDepth} />
          <div className="flex justify-end pt-1">
            <Button
              size="small"
              variant="outline"
              icon={isLooksOpen ? <EyeIcon size={14} /> : <EyeClosedIcon size={14} />}
              onClick={() => setIsLooksOpen(previous => !previous)}
            >
              {isLooksOpen ? 'Hide Looks' : 'Show Looks'}
            </Button>
          </div>
        </div>
      </div>
      <LookGroupLooksPanel groupId={item.group.id} open={isLooksOpen} />
    </motion.article>
  );
};

export default AvailableGroupItem;
